import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ErrorCode,
  PackageType,
  PurchasesError,
  type Offering,
  type Offerings,
  type Package,
} from '@revenuecat/purchases-js'
import { supabase } from '@/lib/supabase'
import { configurePurchases } from '@/lib/purchases'
import { useAuth } from '@/hooks/useAuth'
import { useAircraftGetAll } from '@/lib/hooks'
import { useOrpcTanstackQueryUtils } from '@/lib/orpc'
import { PlanTierCard } from './PlanTierCard'

type Tier = 'solo' | 'core' | 'fleet'
type Billing = 'monthly' | 'annual'

const TIERS: { key: Tier; title: string; aircraft: string; maxAircraft: number; popular?: boolean }[] = [
  { key: 'solo', title: 'Solo', aircraft: '1 aircraft', maxAircraft: 1 },
  { key: 'core', title: 'Core', aircraft: 'Up to 3 aircraft', maxAircraft: 3, popular: true },
  { key: 'fleet', title: 'Fleet', aircraft: 'Up to 15 aircraft', maxAircraft: 15 },
]

interface TierPackages {
  monthly: Package
  annual: Package
}

// Inline Solo/Core/Fleet plan picker shown on the subscription gate when an
// owner's subscription has expired (web only — native links out to the ops
// console instead). Checks out via RevenueCat Web Billing under the signed-in
// owner's user id, so the purchase lands on the same RC customer.
export function PlanSelection() {
  const { userId } = useAuth()
  const queryClient = useQueryClient()
  const orpcUtils = useOrpcTanstackQueryUtils()
  const [offerings, setOfferings] = useState<Offerings | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [billing, setBilling] = useState<Billing>('annual')
  const [purchasingTier, setPurchasingTier] = useState<Tier | null>(null)
  const [purchaseError, setPurchaseError] = useState<string | null>(null)

  // The gate keeps this page reachable while expired, so aircraft data is
  // still served; used to block tiers below the current active fleet size.
  const { data: aircraft = [] } = useAircraftGetAll({ includeArchived: true })
  const activeAircraftCount = aircraft.filter((a) => !a.isArchived).length

  useEffect(() => {
    if (!userId) return
    let cancelled = false

    const load = async () => {
      try {
        const purchases = configurePurchases(userId)
        const result = await purchases.getOfferings()
        if (cancelled) return
        setOfferings(result)
      } catch (err) {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : 'Failed to load plans')
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [userId])

  const tierPackages = useMemo<Record<Tier, TierPackages | null> | null>(() => {
    if (!offerings) return null
    return {
      solo: resolvePackages(offerings.all['solo']),
      core: resolvePackages(offerings.all['default'] ?? offerings.current),
      fleet: resolvePackages(offerings.all['fleet']),
    }
  }, [offerings])

  const ready = !!(tierPackages?.solo && tierPackages.core && tierPackages.fleet)
  const yearlySavePct = tierPackages?.core ? savingsPct(tierPackages.core.annual, tierPackages.core.monthly) : null

  const purchase = async (tierKey: Tier) => {
    const pkg = tierPackages?.[tierKey]?.[billing] ?? null
    if (!pkg || !userId || purchasingTier) return
    const selectedTier = TIERS.find((t) => t.key === tierKey)
    if (selectedTier && activeAircraftCount > selectedTier.maxAircraft) {
      setPurchaseError(downgradeBlockedMessage(activeAircraftCount, selectedTier.maxAircraft, selectedTier.title))
      return
    }
    setPurchasingTier(tierKey)
    setPurchaseError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const purchases = configurePurchases(userId)
      await purchases.purchase({
        rcPackage: pkg,
        customerEmail: session?.user?.email,
        skipSuccessPage: true,
      })
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: orpcUtils.subscription.checkSubscription.key(),
        }),
        queryClient.invalidateQueries({
          queryKey: orpcUtils.subscription.listPurchases.key(),
        }),
      ])
      toast.success('Subscription active')
    } catch (err) {
      if (err instanceof PurchasesError && err.errorCode === ErrorCode.UserCancelledError) {
        // user closed the overlay — stay on the page silently
      } else {
        setPurchaseError(err instanceof Error ? err.message : 'Purchase failed')
      }
    } finally {
      setPurchasingTier(null)
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <h2 className="text-[24px] font-semibold leading-tight text-content-brand-bold">
          Choose a plan
        </h2>
        <p className="text-sm leading-normal text-content-primary-subtle tracking-[0.07px]">
          Continue your Pronteer subscription.
        </p>
      </div>

      <div className="flex items-center gap-2 self-center rounded-full bg-surface-L2 p-[3px]">
        <button
          type="button"
          onClick={() => setBilling('monthly')}
          className={`flex min-h-[29px] items-center rounded-full px-3 py-1 text-base leading-6 transition-colors ${
            billing === 'monthly'
              ? 'bg-surface-L0 font-semibold text-content-primary-bold'
              : 'text-content-primary-subtle'
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => setBilling('annual')}
          className={`flex min-h-[29px] items-center gap-1.5 rounded-full px-3 py-1 text-base leading-6 transition-colors ${
            billing === 'annual'
              ? 'bg-surface-L0 font-semibold text-content-primary-bold'
              : 'text-content-primary-subtle'
          }`}
        >
          Yearly
          {yearlySavePct !== null && (
            <span className="text-xs font-semibold leading-4 text-content-brand-bold">
              Save ~{yearlySavePct}%
            </span>
          )}
        </button>
      </div>

      {loadError ? (
        <p className="text-sm text-destructive">{loadError}</p>
      ) : !ready ? (
        <p className="text-sm text-content-primary-subtle">Loading plans…</p>
      ) : (
        <div className="grid gap-4 pt-2 lg:grid-cols-3">
          {TIERS.map((t) => {
            const pkgs = tierPackages![t.key]!
            const blocked = activeAircraftCount > t.maxAircraft
            return (
              <PlanTierCard
                key={t.key}
                title={t.title}
                price={
                  billing === 'annual'
                    ? formatPerMonth(pkgs.annual)
                    : pkgs.monthly.webBillingProduct.currentPrice.formattedPrice
                }
                priceSuffix="/month"
                features={[
                  t.aircraft,
                  billing === 'annual'
                    ? 'Billed annually · 30 day money-back guarantee'
                    : 'Billed monthly · cancel anytime',
                ]}
                footnote={billing === 'annual' ? annualFootnote(pkgs.annual, pkgs.monthly) : undefined}
                popular={t.popular}
                disabled={blocked}
                disabledNote={
                  blocked
                    ? downgradeBlockedMessage(activeAircraftCount, t.maxAircraft, t.title)
                    : undefined
                }
                ctaLabel={purchasingTier === t.key ? 'Opening checkout…' : `Choose ${t.title}`}
                ctaDisabled={blocked || purchasingTier !== null}
                onCheckout={() => purchase(t.key)}
              />
            )
          })}
        </div>
      )}

      {purchaseError && <p className="text-sm text-destructive">{purchaseError}</p>}

      <p className="self-center text-center text-xs leading-snug text-content-primary-subtle sm:max-w-[400px]">
        By continuing you confirm that you've read our Privacy Policy and agree to the{' '}
        <a
          href="https://pronteer.com/eula"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Pronteer End User Licence Agreement
        </a>
        .
      </p>
    </div>
  )
}

function downgradeBlockedMessage(activeCount: number, maxAircraft: number, tierTitle: string): string {
  return `You have ${activeCount} active aircraft. Reduce them to ${maxAircraft} in the Aircraft page to switch to ${tierTitle}.`
}

function resolvePackages(offering: Offering | null | undefined): TierPackages | null {
  if (!offering) return null
  const monthly =
    offering.monthly ?? offering.availablePackages.find((p) => p.packageType === PackageType.Monthly) ?? null
  const annual =
    offering.annual ?? offering.availablePackages.find((p) => p.packageType === PackageType.Annual) ?? null
  return monthly && annual ? { monthly, annual } : null
}

function formatPerMonth(pkg: Package): string {
  const price = pkg.webBillingProduct.currentPrice
  if (pkg.packageType === PackageType.Annual) {
    const perMonthMicros = price.amountMicros / 12
    return formatCurrency(perMonthMicros, price.currency, 2)
  }
  return price.formattedPrice
}

function annualFootnote(annual: Package, monthly: Package): React.ReactNode {
  const annualPrice = annual.webBillingProduct.currentPrice
  const monthlyPrice = monthly.webBillingProduct.currentPrice
  const savingsMicros = Math.max(0, monthlyPrice.amountMicros * 12 - annualPrice.amountMicros)
  return (
    <>
      {annualPrice.formattedPrice} billed annually
      {savingsMicros > 0 && (
        <span className="text-content-positive-bold"> · Save {formatCurrency(savingsMicros, annualPrice.currency)}</span>
      )}
    </>
  )
}

function savingsPct(annual: Package, monthly: Package): number | null {
  const annualMicros = annual.webBillingProduct.currentPrice.amountMicros
  const yearlyMonthlyMicros = monthly.webBillingProduct.currentPrice.amountMicros * 12
  if (yearlyMonthlyMicros <= 0 || annualMicros >= yearlyMonthlyMicros) return null
  const pct = Math.round(((yearlyMonthlyMicros - annualMicros) / yearlyMonthlyMicros) * 100)
  return pct > 0 ? pct : null
}

function formatCurrency(amountMicros: number, currency: string, fractionDigits = 0): string {
  const amount = amountMicros / 1_000_000
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(fractionDigits)}`
  }
}
